/**
 * Política de recuperación del reproductor HLS (#148). Pura: decide QUÉ
 * hacer ante un error fatal de hls.js; el hook `useHlsPlayer` lo ejecuta.
 *
 * Por qué existe: desde que el vivo se corta cada ~40 s (#147), un 404 de un
 * segmento ya borrado de la ventana rodante o un timeout de playlist es un
 * error fatal normal, no una avería. hls.js trae la recuperación estándar
 * (`startLoad()` para red, `recoverMediaError()` para media); lo que hay
 * que decidir es cuántas veces y con qué espera para no entrar en bucle.
 */

export type HlsErrorType = 'networkError' | 'mediaError' | string;

export type RecoveryAction =
  | { kind: 'reload-network'; delayMs: number }
  | { kind: 'recover-media'; swapAudioCodec: boolean; delayMs: number }
  | { kind: 'give-up' };

export interface RecoveryState {
  /** Errores fatales seguidos (red + media) desde la última reproducción sana. */
  attempts: number;
  /** Cuántas veces se intentó `recoverMediaError` en esta racha. */
  mediaRecoveries: number;
}

/** Tras este número de errores fatales seguidos se muestra el error al usuario. */
export const MAX_RECOVERY_ATTEMPTS = 6;
const BASE_DELAY_MS = 1000;
const MAX_DELAY_MS = 8000;
/** hls.js recomienda: 1.º recoverMediaError, 2.º swapAudioCodec + recover, después rendirse. */
const MAX_MEDIA_RECOVERIES = 2;

export function freshRecovery(): RecoveryState {
  return { attempts: 0, mediaRecoveries: 0 };
}

export function nextRecovery(
  state: RecoveryState,
  errorType: HlsErrorType,
): { action: RecoveryAction; state: RecoveryState } {
  if (state.attempts >= MAX_RECOVERY_ATTEMPTS) {
    return { action: { kind: 'give-up' }, state };
  }
  if (errorType === 'networkError') {
    const delayMs = Math.min(MAX_DELAY_MS, BASE_DELAY_MS * 2 ** state.attempts);
    return {
      action: { kind: 'reload-network', delayMs },
      state: { ...state, attempts: state.attempts + 1 },
    };
  }
  if (errorType === 'mediaError') {
    if (state.mediaRecoveries >= MAX_MEDIA_RECOVERIES) {
      return { action: { kind: 'give-up' }, state };
    }
    return {
      action: { kind: 'recover-media', swapAudioCodec: state.mediaRecoveries > 0, delayMs: 0 },
      state: { attempts: state.attempts + 1, mediaRecoveries: state.mediaRecoveries + 1 },
    };
  }
  return { action: { kind: 'give-up' }, state };
}
